import { Card } from 'reactstrap';
import { Modal, addToast, showDialogInfo } from '@octano/global-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';
import { PerformanceAnalysisFilters } from './parts/PerformanceAnalysisFilters';
import {
  PerformanceAnalysisFiltersConsumer,
  PerformanceAnalysisFiltersLoader,
} from './parts/PerformanceAnalysisFiltersLoader';
import {
  PerformanceAnalysisListConsumer,
  PerformanceAnalysisListLoader,
} from './parts/PerformanceAnalysisListLoader';
import { PerformanceAnalysisTable } from './parts/PerformanceAnalysisTable';
import { PerformanceAnalysisTablePagination } from './parts/PerformanceAnalysisTablePagination';
import {
  PerformanceAnalysis,
  PerformanceAnalysisDetailRowModalData,
} from './types';
import { PerformanceAnalysisTableModal } from './parts/PerformanceAnalysisTableModal';
import Loading from '../../../components/info/Loading';
import { downloadFromBlob } from '../../../utils/blob';
import { downloadSectionDetail } from './api';
import { PerformanceAnalysisPeriodLoader } from './parts/PerformanceAnalysisPeriodLoader';
import { PerformanceAnalysisPeriod } from './parts/PerformanceAnalysisPeriod';
import { PerformanceAnalysisFiltersDownload } from './parts/PerformanceAnalysisFiltersDownload';

function PerformanceAnalysisList() {
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_PERFORMANCE_ANALYSIS,
  );
  const [
    modalData,
    setModalData,
  ] = useState<PerformanceAnalysisDetailRowModalData>();

  const [downloading, setDownloading] = useState(false);
  const [listDownloading, setListDownloading] = useState(false);

  const onShowModal = useCallback(
    (data: PerformanceAnalysisDetailRowModalData) => {
      setModalData(data);
    },
    [],
  );

  const handleDownload = useCallback(
    async (row: PerformanceAnalysis) => {
      if (downloading) {
        return;
      }
      setDownloading(true);
      const { data, error, status } = await downloadSectionDetail(
        row.sectionId,
      );
      setDownloading(false);

      if (status === 204) {
        showDialogInfo({
          icon: {
            name: 'information',
            color: 'primary',
          },
          title: t(`sectionDownload.empty.title`),
          subtitle: t(`sectionDownload.empty.description`),
          btnConfirm: {
            text: t(`sectionDownload.empty.confirm`),
            color: 'primary',
            onConfirm: () => {},
          },
        });
      } else if (data && !error) {
        addToast({
          icon: 'download',
          color: 'success',
          text: t(`sectionDownload.success`, { section: row.sectionCode }),
        });
        const blob = new Blob([data], { type: 'vnd.ms-excel' });
        downloadFromBlob(
          blob,
          `${t(`sectionDownload.filename`, { section: row.sectionCode })}.xlsx`,
        );
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`sectionDownload.error`, { section: row.sectionCode }),
        });
      }
    },
    [downloading, t],
  );

  const onCloseModal = useCallback(() => {
    setModalData(undefined);
  }, []);
  return (
    <>
      <PerformanceAnalysisPeriodLoader>
        {(period) => (
          <Card className="mx-3 p-4">
            <p className="fs-16 text-light">{t('description')}</p>
            <PerformanceAnalysisFiltersLoader periodId={period.id}>
              <PerformanceAnalysisPeriod />
              <PerformanceAnalysisFiltersConsumer>
                <PerformanceAnalysisListLoader>
                  <PerformanceAnalysisFilters />
                  <div className="text-right mb-3">
                    <PerformanceAnalysisFiltersDownload
                      downloading={listDownloading}
                      onDownloadChange={setListDownloading}
                    />
                  </div>
                  <PerformanceAnalysisListConsumer>
                    {({ data, loading }) => {
                      return (
                        <>
                          <PerformanceAnalysisTable
                            data={data?.data ?? []}
                            loading={loading}
                            onShowModal={onShowModal}
                            onDownload={handleDownload}
                          />
                          {!!data && !loading && data.total_pages > 1 && (
                            <PerformanceAnalysisTablePagination
                              total={data.total}
                              totalPages={data.total_pages}
                            />
                          )}
                        </>
                      );
                    }}
                  </PerformanceAnalysisListConsumer>
                </PerformanceAnalysisListLoader>
              </PerformanceAnalysisFiltersConsumer>
            </PerformanceAnalysisFiltersLoader>
          </Card>
        )}
      </PerformanceAnalysisPeriodLoader>
      {!!modalData && (
        <PerformanceAnalysisTableModal
          data={modalData}
          onClose={onCloseModal}
        />
      )}
      {!!downloading && (
        <Modal isOpen toggle={() => {}} closeOnBackdrop={false}>
          <Loading />
        </Modal>
      )}
    </>
  );
}

export default PerformanceAnalysisList;
